import React from 'react';
import { Link, type LinkProps, Navigate, type NavigateProps } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getLocaleFromPathname,
  type AppLocale,
  withLocale,
} from '../i18n/localeRouting';
import { useLocation } from 'react-router-dom';
import { scrollPageToTop } from '../utils/scrollToTop';

export function useAppLocale(): AppLocale {
  const { pathname } = useLocation();
  return getLocaleFromPathname(pathname);
}

export function useLocalizedPath() {
  const locale = useAppLocale();
  return (path: string) => withLocale(path, locale);
}

type LocalizedLinkProps = Omit<LinkProps, 'to'> & {
  to: string;
  locale?: AppLocale;
};

export const LocalizedLink = React.forwardRef<HTMLAnchorElement, LocalizedLinkProps>(
  function LocalizedLink({ to, locale: localeProp, onClick, ...rest }, ref) {
    const currentLocale = useAppLocale();
    const { pathname } = useLocation();
    const locale = localeProp ?? currentLocale;
    const target = withLocale(to, locale);

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      onClick?.(e);
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        return;
      }
      if (rest.target && rest.target !== '_self') return;
      if (/[?#]/.test(target) || target !== pathname) return;
      e.preventDefault();
      scrollPageToTop('smooth');
    };

    return <Link ref={ref} to={target} onClick={handleClick} {...rest} />;
  }
);

type LocalizedNavigateProps = Omit<NavigateProps, 'to'> & {
  to: string;
};

export function LocalizedNavigate({ to, ...rest }: LocalizedNavigateProps) {
  const locale = useAppLocale();
  return <Navigate to={withLocale(to, locale)} {...rest} />;
}

/** i18n language를 URL locale과 맞출 때 사용 */
export function useSyncI18nLocale(locale: AppLocale) {
  const { i18n } = useTranslation();
  React.useEffect(() => {
    if (i18n.language !== locale) {
      void i18n.changeLanguage(locale);
    }
  }, [i18n, locale]);
}
